import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Container,
  Menu,
  MenuItem,
  Divider,
  Popover,
  Paper,
} from '@mui/material';
import { Download, Tune, Info, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Logo from '../shared/Logo';
import ExportButton from './ExportButton';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import tokens from '../../theme/tokens';
import { enhancedExportJSON } from '../../services/ImportExportUtils';
import { useSpecialEffect } from '../../context/SpecialEffectContext';
import { useBarStyle } from '../../context/BarStyleContext';
import { useDesign } from '../../context/DesignContext';
import { useLanguage } from '../../localization';
import { useBackupReminder } from '../../hooks/useBackupReminder';

function Header({
  openTypographySettings,
  isNarrowDesktop,
  showSettingsInEditor,
  toggleEditorView,
  isMobile,
  formData,
  selectedLayout,
  selectedStyle,
  selectedBackground,
  typographySettings,
  selectedSections,
  setSelectedSections,
}) {
  const { selectedSpecialEffectId } = useSpecialEffect();
  const { selectedBarStyleId } = useBarStyle();
  const { currentColors } = useDesign();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // State for info menu
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const infoMenuOpen = Boolean(infoAnchorEl);
  const saveButtonRef = useRef(null);

  // Use the backup reminder hook
  const {
    showReminder: backupReminderOpen,
    reminderAnchor: backupReminderAnchor,
    hideReminder: handleBackupReminderClose,
    handleSaveAction,
    setReminderAnchor,
  } = useBackupReminder({
    formData,
    isMobile,
  });

  // Set anchor when component mounts and reminder should show
  React.useEffect(() => {
    if (backupReminderOpen && !backupReminderAnchor && saveButtonRef.current) {
      setReminderAnchor(saveButtonRef.current);
    }
  }, [backupReminderOpen, backupReminderAnchor, setReminderAnchor]);

  const handleExportJson = () => {
    enhancedExportJSON(
      formData,
      selectedLayout,
      currentColors,
      selectedStyle,
      selectedBackground,
      typographySettings,
      selectedSpecialEffectId,
      selectedBarStyleId,
      selectedSections
    );
  };

  const handleInfoClick = (event) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  const handleLegalPageNavigation = (path) => {
    navigate(path);
    handleInfoClose();
  };

  const handleBackupReminderSave = () => {
    handleSaveAction(handleExportJson);
  };

  return (
    <AppBar
      position="static"
      elevation={1}
      sx={{
        backgroundColor: tokens.colors.alpha.black[800],
        backdropFilter: 'blur(8px)',
        boxShadow: tokens.shadows.sm,
        color: tokens.colors.neutral[800],
        width: '100%',
        borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar 
          sx={{ 
            justifyContent: tokens.components.header.layout.justifyContent,
            alignItems: tokens.components.header.layout.alignItems,
            padding: isMobile ? tokens.components.header.padding.mobile : tokens.components.header.padding.desktop,
            minHeight: tokens.components.header.layout.minHeight,
            maxHeight: tokens.components.header.layout.maxHeight,
            height: tokens.components.header.height,
          }}
        >
          {/* Logo */}
          <Logo 
            size="medium"
            sx={{ 
              px: isMobile ? tokens.components.header.logo.padding.mobile : tokens.components.header.logo.padding.desktop,
              flexShrink: tokens.components.header.logo.flexShrink
            }}
          />
          
          {/* Mobile Language Switcher - Only show on mobile */}
          {isMobile && (
            <Box sx={{ ml: 'auto', mr: 1 }}>
              <LanguageSwitcher 
                variant="compact" 
                size="small" 
                showLabel={false}
              />
            </Box>
          )}
          
          {/* Info Icon - Always visible */}
          <Tooltip title="Legal Information">
            <IconButton
              onClick={handleInfoClick}
              size="small"
              sx={{ 
                ml: isMobile ? 0 : 'auto',
                mr: 2,
                color: tokens.colors.neutral[700],
                '&:hover': {
                  color: tokens.colors.primary.main,
                  backgroundColor: tokens.colors.primary.main + '10',
                }
              }}
            >
              <Info fontSize='small'/> 
            </IconButton>
          </Tooltip>

          {/* Info Menu */}
          <Menu
            anchorEl={infoAnchorEl}
            open={infoMenuOpen}
            onClose={handleInfoClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: tokens.shadows.lg,
                borderRadius: tokens.radius.base,
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem 
              onClick={() => handleLegalPageNavigation('/datenschutz')}
              sx={{ fontSize: '0.875rem' }}
            >
              {t('footer.legal.privacy')}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLegalPageNavigation('/nutzungsbedingungen')}
              sx={{ fontSize: '0.875rem' }}
            >
              {t('footer.legal.terms')}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLegalPageNavigation('/impressum')}
              sx={{ fontSize: '0.875rem' }}
            >
              {t('footer.legal.impressum')}
            </MenuItem>
            <Divider />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.secondary }}>
                © {new Date().getFullYear()} privacv
              </Typography>
            </Box>
          </Menu>
          
          {isNarrowDesktop && (
            <Tooltip title={showSettingsInEditor ? t('header.showEditorInputs') : t('header.showSettings')}>
              <IconButton
                onClick={toggleEditorView}
                size="small"
                sx={{ 
                    ml: 1,
                    color: tokens.colors.neutral[700],
                    backgroundColor: showSettingsInEditor ? tokens.colors.primary.main + '10' : 'transparent',
                    '&:hover': {
                        color: tokens.colors.primary.main,
                        backgroundColor: tokens.colors.primary.main + '10',
                    }
                }}
              >
                <Tune fontSize='small'/> 
              </IconButton>
            </Tooltip>
          )}

          {/* Action Buttons */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: tokens.components.header.navigation.gap.desktop,
              flexShrink: 0
            }}>
              <LanguageSwitcher 
                variant="compact" 
                size="small" 
                showLabel={false}
              />
              
              <Button
                ref={saveButtonRef}
                data-umami-event = "Save File Button"
                variant="contained"
                onClick={handleExportJson}
                startIcon={<Download />}
                sx={{
                  minWidth: tokens.components.header.buttons.minWidth,
                  px: isMobile ? tokens.components.header.buttons.padding.mobile.split(' ')[1] : tokens.components.header.buttons.padding.desktop.split(' ')[1],
                  py: isMobile ? tokens.components.header.buttons.padding.mobile.split(' ')[0] : tokens.components.header.buttons.padding.desktop.split(' ')[0],
                  fontSize: tokens.components.header.buttons.fontSize,
                  textTransform: 'none',
                  backgroundColor: tokens.colors.primary.main,
                  '&:hover': {
                    backgroundColor: tokens.colors.primary.dark
                  }
                }}
              >
                {t('header.saveFile')}
              </Button>

              <ExportButton
                variant="contained"
                selectedSections={selectedSections}
                setSelectedSections={setSelectedSections}
                sx={{
                  minWidth: tokens.components.header.buttons.minWidth,
                  px: isMobile ? tokens.components.header.buttons.padding.mobile.split(' ')[1] : tokens.components.header.buttons.padding.desktop.split(' ')[1],
                  py: isMobile ? tokens.components.header.buttons.padding.mobile.split(' ')[0] : tokens.components.header.buttons.padding.desktop.split(' ')[0],
                  fontSize: tokens.components.header.buttons.fontSize,
                  textTransform: 'none',
                  backgroundColor: tokens.colors.primary.main,
                  '&:hover': {
                    backgroundColor: tokens.colors.primary.dark
                  }
                }}
              />
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Backup Reminder Popover */}
      <Popover
        open={backupReminderOpen}
        anchorEl={backupReminderAnchor}
        onClose={handleBackupReminderClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            maxWidth: 280,
            boxShadow: tokens.shadows.lg,
            borderRadius: tokens.radius.base,
            border: `1px solid ${tokens.colors.primary.main}20`,
          }
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.colors.primary.main }}>
              {t('header.backupReminder.title')}
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleBackupReminderClose}
              sx={{ ml: 1, mt: -0.5, color: tokens.colors.neutral[500] }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ mb: 2, color: tokens.colors.neutral[700] }}>
            {t('header.backupReminder.message')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button 
              size="small" 
              onClick={handleBackupReminderClose}
              sx={{ color: tokens.colors.neutral[600] }}
            >
              {t('header.backupReminder.dismiss')}
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              onClick={handleBackupReminderSave}
              sx={{ 
                backgroundColor: tokens.colors.primary.main,
                '&:hover': { backgroundColor: tokens.colors.primary.dark }
              }}
            >
              {t('header.backupReminder.saveNow')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </AppBar>
  );
}

export default Header;